import { useState, useEffect } from 'react';
import * as Store from '@apply/store';
import { Question, QuestionType, PageQuestionProps, stateSelectorValues, countyList } from '@apply/ui';
import { connect } from 'react-redux';

export interface PropertyAddressPageQuestionProps extends PageQuestionProps {
    onChangeStreet: (guid: string, value: string) => void;
    onChangeStreet2: (guid: string, value: string) => void;
    onChangeCity: (guid: string, value: string) => void;
    onChangeState: (guid: string, value: string) => void;
    onChangeCounty: (guid: string, value: string) => void;
    onChangeZip: (guid: string, value: string) => void;
}

const PropertyAddress: React.FC<PropertyAddressPageQuestionProps> = ({
    onQuestionValidate,
    submitted,
    property,
    onChangeStreet,
    onChangeStreet2,
    onChangeCity,
    onChangeState,
    onChangeCounty,
    onChangeZip,
    required,
}) => {
    const [countySelectList, setCountySelectList] = useState<{ value: string; display: string }[]>();

    useEffect(() => {
        const tempCountyList = countyList
            .filter((county) => county.state === property?.address?.state)
            .map((county) => {
                return { display: county.county, value: county.county };
            });
        tempCountyList.unshift({ display: '', value: '' });
        setCountySelectList(tempCountyList);
    }, [property?.address?.state]);

    return (
        <>
            <div className='form-group'>
                <div className='row'>
                    <Question
                        type={QuestionType.Text}
                        submitted={submitted}
                        selector='propertyAddressLine1'
                        placeholder='123 Main Street'
                        styleName='grouped'
                        onQuestionValidate={onQuestionValidate}
                        value={property?.address?.street1}
                        onChangeQualifier={property.guid}
                        onChange={onChangeStreet}
                        required={required}
                    >
                        <span className='sr-only'>Property</span> Street Address
                    </Question>
                    <Question
                        type={QuestionType.Text}
                        submitted={submitted}
                        selector='propertyAddressLine2'
                        placeholder='Suite 123'
                        styleName='grouped'
                        value={property?.address?.street2}
                        onChangeQualifier={property.guid}
                        onChange={onChangeStreet2}
                    >
                        <span className='sr-only'>Property Street Address, Line 2</span> &nbsp;
                    </Question>
                </div>
            </div>
            <div className='form-group'>
                <div className='row'>
                    <div className='columns small-6 medium-3'>
                        <Question
                            type={QuestionType.Zipcode}
                            submitted={submitted}
                            selector='propertyAddressZipcode'
                            placeholder='01234'
                            styleName='grouped'
                            onQuestionValidate={onQuestionValidate}
                            value={property?.address?.zipCode}
                            onChangeQualifier={property.guid}
                            onChange={onChangeZip}
                            required={required}
                        >
                            <span className='sr-only'>Property</span> Zip
                        </Question>
                    </div>
                    <div className='columns small-6 medium-3'>
                        <Question
                            type={QuestionType.Text}
                            submitted={submitted}
                            selector='propertyAddressCity'
                            placeholder='Hamilton'
                            styleName='grouped'
                            onQuestionValidate={onQuestionValidate}
                            value={property?.address?.city}
                            onChangeQualifier={property.guid}
                            onChange={onChangeCity}
                            required={required}
                        >
                            <span className='sr-only'>Property</span> City
                        </Question>
                    </div>
                    <div className='columns small-6 medium-3'>
                        <Question
                            type={QuestionType.ColumnSelect}
                            submitted={submitted}
                            selector='propertyAddressState'
                            styleName='grouped'
                            onQuestionValidate={onQuestionValidate}
                            value={property?.address?.state}
                            values={stateSelectorValues}
                            onChangeQualifier={property.guid}
                            onChange={onChangeState}
                            required={required}
                        >
                            <span className='sr-only'>Property</span> State
                        </Question>
                    </div>
                    <div className='columns small-6 medium-3'>
                        <Question
                            type={QuestionType.ColumnSelect}
                            submitted={submitted}
                            selector='propertyAddressCounty'
                            styleName='grouped'
                            onQuestionValidate={onQuestionValidate}
                            value={property?.address?.county}
                            values={countySelectList}
                            onChangeQualifier={property.guid}
                            onChange={onChangeCounty}
                            required={required}
                        >
                            <span className='sr-only'>Property</span> County
                        </Question>
                    </div>
                </div>
            </div>
        </>
    );
};

export const mapStateToProps = (state: Store.ApplyRootStateType) => {
    return {
        property: state.properties.items.find((itm) => itm.guid === state.properties.currentPropertyId),
    };
};

export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: {
        onChangeStreet: (guid: string, value: string) => void;
        onChangeStreet2: (guid: string, value: string) => void;
        onChangeCity: (guid: string, value: string) => void;
        onChangeState: (guid: string, value: string) => void;
        onChangeCounty: (guid: string, value: string) => void;
        onChangeZip: (guid: string, value: string) => void;
        dispatch: Store.ApplyDispatchType;
    } = {
        onChangeStreet: (guid: string, value: string) => dispatch(Store.PropertiesStore.Actions.updateAddressLine1(guid, value)),
        onChangeStreet2: (guid: string, value: string) => dispatch(Store.PropertiesStore.Actions.updateAddressLine2(guid, value)),
        onChangeCity: (guid: string, value: string) => dispatch(Store.PropertiesStore.Actions.updateAddressCity(guid, value)),
        onChangeState: (guid: string, value: string) => dispatch(Store.PropertiesStore.Actions.updateAddressState(guid, value)),
        onChangeCounty: (guid: string, value: string) => dispatch(Store.PropertiesStore.Actions.updateAddressCounty(guid, value)),
        onChangeZip: (guid: string, value: string) => dispatch(Store.PropertiesStore.Actions.updateAddressZipcode(guid, value)),
        dispatch,
    };
    return props;
};

export default connect(mapStateToProps, mapDispatchToProps)(PropertyAddress);
